/**
 * EtifoodQuadraturaDialog — verifica di quadratura Vendite DDT vs Etiflor:
 * differenze mensili testata/righe, DDT senza prezzi, dati stantii e numeri mai importati.
 */
import React, { useEffect, useState } from "react";
import axios from "axios";
import { Scale, AlertTriangle, CheckCircle2, RefreshCw } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const eur = (n) => (n ?? 0).toLocaleString("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtDate = (d) => (d ? d.split("-").reverse().join("/") : "—");
const fmtMonth = (m) => {
  if (!m) return "—";
  const [y, mm] = m.split("-");
  const names = ["Gen", "Feb", "Mar", "Apr", "Mag", "Giu", "Lug", "Ago", "Set", "Ott", "Nov", "Dic"];
  return `${names[parseInt(mm, 10) - 1]} ${y}`;
};

export default function EtifoodQuadraturaDialog({ open, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    axios.get(`${API}/etifood-sales/quadratura`)
      .then((r) => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [open]);

  const anomalie = (data?.zero_price?.length || 0) + (data?.header_lt_lines?.length || 0);

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-4xl max-h-[92vh] overflow-y-auto" data-testid="quadratura-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Scale className="w-5 h-5 text-[#8A1C2A]" /> Verifica quadratura DDT ↔ Etiflor
          </DialogTitle>
        </DialogHeader>

        {loading && <p className="p-6 text-center text-sm text-stone-500">Analisi in corso…</p>}
        {!loading && !data && <p className="p-6 text-center text-sm text-red-600">Errore nel caricamento</p>}

        {!loading && data && (
          <div className="space-y-4">
            <div className="p-3 rounded-lg bg-blue-50 border border-blue-200 text-xs text-blue-900 flex items-start gap-2">
              <RefreshCw className="w-4 h-4 mt-0.5 shrink-0" />
              <span>
                Per correggere un periodo che non quadra: esporta da Etiflor il <b>DDT DETTAGLIO</b> di quel
                periodo e re-importalo qui — i DDT già presenti vengono <b>aggiornati automaticamente</b> con
                i dati più recenti (prezzi inseriti dopo, correzioni), mai duplicati.
              </span>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-center">
              <div className="p-3 rounded-lg border bg-stone-50 border-stone-200">
                <p className="text-lg font-bold text-stone-900" data-testid="quad-count">{data.totals.count}</p>
                <p className="text-[11px] text-stone-500">DDT in archivio</p>
              </div>
              <div className="p-3 rounded-lg border bg-stone-50 border-stone-200">
                <p className="text-lg font-bold text-stone-900" data-testid="quad-header">{eur(data.totals.header)} €</p>
                <p className="text-[11px] text-stone-500">Totale documenti (Etiflor)</p>
              </div>
              <div className="p-3 rounded-lg border bg-stone-50 border-stone-200">
                <p className="text-lg font-bold text-stone-900" data-testid="quad-lines">{eur(data.totals.lines)} €</p>
                <p className="text-[11px] text-stone-500">Totale righe articoli</p>
              </div>
              <div className={`p-3 rounded-lg border ${anomalie > 0 ? "bg-amber-50 border-amber-200" : "bg-emerald-50 border-emerald-200"}`}>
                <p className={`text-lg font-bold ${anomalie > 0 ? "text-amber-800" : "text-emerald-700"}`} data-testid="quad-anomalie">{anomalie}</p>
                <p className="text-[11px] text-stone-500">DDT da re-importare</p>
              </div>
            </div>

            {data.zero_price.length > 0 && (
              <div className="p-2.5 rounded-lg bg-red-50 border border-red-200" data-testid="quad-zero-price">
                <p className="text-sm font-semibold text-red-700 flex items-center gap-1.5 mb-1.5">
                  <AlertTriangle className="w-4 h-4" /> {data.zero_price.length} DDT con totale a 0 € (prezzi non inseriti oppure autoconsumi/omaggi)
                </p>
                <div className="max-h-40 overflow-y-auto space-y-0.5">
                  {data.zero_price.map((d, i) => (
                    <p key={i} className="text-xs text-stone-700">
                      DDT n. <b className="font-mono">{d.number}</b> · {fmtDate(d.date)} · {d.customer_name || "—"}
                    </p>
                  ))}
                </div>
                <p className="text-[11px] text-red-600 mt-1.5">→ se non sono autoconsumi/omaggi, ri-esporta questi periodi da Etiflor e re-importa il file</p>
              </div>
            )}

            {data.header_lt_lines.length > 0 && (
              <div className="p-2.5 rounded-lg bg-amber-50 border border-amber-200" data-testid="quad-header-lt">
                <p className="text-sm font-semibold text-amber-800 flex items-center gap-1.5 mb-1.5">
                  <AlertTriangle className="w-4 h-4" /> {data.header_lt_lines.length} DDT con totale documento inferiore alle righe (dati non allineati)
                </p>
                <div className="max-h-40 overflow-y-auto space-y-0.5">
                  {data.header_lt_lines.map((d, i) => (
                    <p key={i} className="text-xs text-stone-700">
                      DDT n. <b className="font-mono">{d.number}</b> · {fmtDate(d.date)} · {d.customer_name || "—"} ·
                      doc <b>{eur(d.total_net)} €</b> vs righe <b>{eur(d.lines_net)} €</b> ({eur(d.diff)} €)
                    </p>
                  ))}
                </div>
              </div>
            )}

            {(data.sequence || []).some((s) => s.missing_count > 0) && (
              <div className="p-2.5 rounded-lg bg-stone-50 border border-stone-200" data-testid="quad-missing">
                <p className="text-sm font-semibold text-stone-800 mb-1.5">Numeri DDT mai importati (buchi nella numerazione Etiflor)</p>
                {data.sequence.filter((s) => s.missing_count > 0).map((s) => (
                  <div key={s.year} className="mb-1.5">
                    <p className="text-xs text-stone-500 mb-1">Anno {s.year} — {s.missing_count} mancanti (dal n. {s.min} al n. {s.max})</p>
                    <div className="flex flex-wrap gap-1">
                      {s.missing_ranges.slice(0, 60).map((r, i) => (
                        <Badge key={i} variant="outline" className="font-mono text-[10px] text-stone-700 border-stone-300">
                          {r[0] === r[1] ? r[0] : `${r[0]}–${r[1]}`}
                        </Badge>
                      ))}
                      {s.missing_ranges.length > 60 && <span className="text-[10px] text-stone-400">…</span>}
                    </div>
                  </div>
                ))}
              </div>
            )}

            <div className="border border-stone-200 rounded-lg overflow-hidden">
              <table className="w-full text-xs">
                <thead className="bg-stone-50 border-b border-stone-200">
                  <tr>
                    <th className="text-left px-3 py-2 font-semibold text-stone-600">Mese</th>
                    <th className="text-right px-3 py-2 font-semibold text-stone-600">DDT</th>
                    <th className="text-right px-3 py-2 font-semibold text-stone-600">Tot. documenti €</th>
                    <th className="text-right px-3 py-2 font-semibold text-stone-600">Tot. righe €</th>
                    <th className="text-right px-3 py-2 font-semibold text-stone-600">Differenza €</th>
                  </tr>
                </thead>
                <tbody>
                  {data.months.map((m) => (
                    <tr key={m.month} className="border-b border-stone-100" data-testid={`quad-month-${m.month}`}>
                      <td className="px-3 py-1.5 font-medium text-stone-800">{fmtMonth(m.month)}</td>
                      <td className="px-3 py-1.5 text-right text-stone-600">{m.count}</td>
                      <td className="px-3 py-1.5 text-right font-mono">{eur(m.header)}</td>
                      <td className="px-3 py-1.5 text-right font-mono">{eur(m.lines)}</td>
                      <td className={`px-3 py-1.5 text-right font-mono font-semibold ${m.diff < -0.02 ? "text-red-600" : m.diff > 2 ? "text-amber-700" : "text-emerald-700"}`}>
                        {m.diff > 0 ? "+" : ""}{eur(m.diff)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="text-[11px] text-stone-400 -mt-2">
              Una piccola differenza positiva è normale (spese trasporto/incasso a testata + arrotondamenti).
              Differenze <span className="text-red-600 font-semibold">negative</span> = dati da re-importare.
            </p>

            {anomalie === 0 && !((data.sequence || []).some((s) => s.missing_count > 0)) && (
              <p className="flex items-center gap-1.5 text-sm text-emerald-700" data-testid="quad-ok">
                <CheckCircle2 className="w-4 h-4" /> Nessuna anomalia: l'archivio è allineato con Etiflor
              </p>
            )}
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="quad-close">Chiudi</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
